import { useState, useRef, useEffect, memo, ImgHTMLAttributes } from "react";

interface LazyImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  sizes?: string;
}

/**
 * LazyImage - Performance-optimized image component
 * 
 * Key optimizations:
 * - Desktop: Larger preload margin for smoother UX
 * - Mobile: Smaller rootMargin (50px) to save data
 * - Native lazy loading fallback
 * - Explicit dimensions prevent CLS (Core Web Vital)
 * - decoding="async" for non-blocking decode
 * - fetchPriority for above-the-fold images
 * - Memoized to prevent unnecessary re-renders
 * - Uses skeleton-loading for better visual feedback
 */
function LazyImageComponent({ 
  src, 
  alt, 
  className = "", 
  width, 
  height,
  priority = false,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  ...props
}: LazyImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(priority);

  useEffect(() => {
    if (priority) return;

    const img = imgRef.current;
    if (!img) return;

    // Device-aware rootMargin
    // Desktop: Larger margin for smoother loading
    // Mobile: Smaller margin to save data
    const isDesktop = window.innerWidth >= 1024;
    const isMobile = window.innerWidth < 768;
    const rootMargin = isDesktop ? "300px" : isMobile ? "50px" : "150px";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold: 0.01,
      }
    );

    observer.observe(img);

    return () => observer.disconnect();
  }, [priority]);

  // Calculate aspect ratio for placeholder sizing
  const aspectRatio = width && height ? width / height : undefined;

  return (
    <div 
      className={`relative ${className}`} 
      style={{ 
        width: width ? `${width}px` : undefined,
        maxWidth: '100%',
        height: height ? `${height}px` : undefined,
        // Use aspect-ratio for responsive sizing when dimensions are known
        aspectRatio: aspectRatio && !height ? `${width} / ${height}` : undefined,
        contain: 'layout style',
      }}
    >
      {!isLoaded && (
        <div 
          className="absolute inset-0 skeleton-loading rounded-lg" 
          style={{ 
            paddingBottom: aspectRatio && !height ? `${(1/aspectRatio) * 100}%` : undefined 
          }}
          aria-hidden="true"
        />
      )}
      <img
        ref={imgRef}
        src={isVisible ? src : undefined}
        data-src={src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        className={`transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"} ${className}`}
        onLoad={() => setIsLoaded(true)}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        {...props}
      />
    </div>
  );
}

export const LazyImage = memo(LazyImageComponent);
export default LazyImage;
